﻿import React, { useState, useEffect } from 'react';
import TextSymbolEditor from './TextSymbolEditor';
import { ItemBasicInformation } from '../models/item-basic-information';
import { MolecularData } from '../models/molecular-data';
import { LookupItem } from '../models/lookup-item';
import { MolecularDataFieldUpdate } from '../models/molecular-data-field-update';
import { SymbolEditorProps } from '../models/symbol-editor-props';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { FieldUpdateType, ImageFormatType, ImageType, ModalMessageType, OpenBabelConvertFormats } from '../models/enums';
import { TextSymbolEditorData } from '../models/text-symbol-editor-data';
import { OpenBabelRequest } from '../models/Chemistry/open-babel-request';
import { AttachedImage } from '../models/attached-image';
import DmaFooter from './DmaFooter';

import Button from 'devextreme-react/button';
import { TextBox, Button as TextBoxButton } from 'devextreme-react/text-box';
import TextArea from 'devextreme-react/text-area';
import SelectBox from 'devextreme-react/select-box';
import NumberBox from 'devextreme-react/number-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';

import {
    Validator,
    StringLengthRule,
    RangeRule
} from 'devextreme-react/validator';

let key = 1;

export interface Props {
    productId?: string;
    canEdit: boolean;
}

/*
TODO: 
decide if need to show the SVG also
make images downloadable?
decide if use .MOL file
need any itemBasicInformation/Audit?? 
*/

export default function MolecularDataComponent(props: Props) {
    const [molecularData, setMolecularData] =
        useState<MolecularData>();

    const [openBabelTypes, setOpenBabelTypes] =
        useState<LookupItem[]>();

    const [openBabelInputType, setOpenBabelInputType] =
        useState<LookupItem>();

    const [openBabelOutputType, setOpenBabelOutputType] =
        useState<LookupItem>();

    const [openBabelInputString, setOpenBabelInputString] =
        useState<string>();

    //const [casDataLoaderVisible, setCasDataLoaderVisible] =
    //  useState<boolean>();

    const [molecularSvgImageXml, setMolecularSvgImageXml] =
        useState<string>();

    //keep for now:
    const [molecularSvgImageBase64, setMolecularSvgImageBase64] =
        useState<string>();

    const [openBabelMolecularData, setOpenBabelMolecularData] =
        useState<string>();

    const [symbolEditorProps, setSymbolEditorProps] =
        useState<SymbolEditorProps>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>(false);

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.productId]);

    key++;

    const chemicalFormulaEditorButton = {
        text: 'Ω',
        stylingMode: 'text',
        width: 32,
        hint: "Insert Symbol",
        elementAttr: {
            id: 'chemicalFormula'
        },
        onClick: (e: any) => {
            chemicalFormulaTextSymbolButton(e);
        }
    }

    const renderMolecularSvgImage = () => {
        if (molecularData?.svgImage?.base64Bytes) {
            return <img className="molecular-image-medium" alt="svg molecular"
                src={`data:image/svg+xml;base64,${molecularData.svgImage.base64Bytes}`} />
        }
    };

    const renderMolecularPngImage = () => {
        if (molecularData?.image?.base64Blob) {
            let source = `data:image/png;base64, ${molecularData.image.base64Blob}`;
            return <img className="molecular-image-medium" alt="png molecular" src={source} />;
        }
    };

    const renderOpenBabelMolecularSvgImage = () => {
        if (molecularSvgImageXml) {
            return <img className="molecular-image-medium" alt="svg molecular"
                src={`data:image/svg+xml;utf8,${molecularSvgImageXml}`} />
        }
    };

    /* could be better/easier:
    const renderOpenBabelMolecularSvgImageBase64 = () => {
        if (molecularSvgImageBase64) {
            return <img className="molecular-image-medium" alt="svg molecular"
                src={`data:image/svg+xml;base64,${molecularSvgImageBase64}`} />
        }
    };
    */

    return (
        <div className="pw-form-container">

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">CAS #</div>
                    <TextBox id="casNumber" className="textbox"
                        readOnly={!props.canEdit}
                        onValueChanged={molecularDataFieldChanged}
                        placeholder='NA'
                        value={molecularData && molecularData.casNumber ? molecularData.casNumber : undefined} />
                </div>

                {/*
                <Button id="getCasDataButton" disabled={!props.canEdit} className="pw-row-button-08" height={'1.6rem'}
                    type="success" text="Get CAS Data" onClick={showCasDataloader} />
                    */}
            </div>

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">Molecular Formula</div>
                <TextBox id="formula" className="textbox"
                    showClearButton={true} readOnly={!props.canEdit}
                    onValueChanged={molecularDataFieldChanged}
                    placeholder='NA'
                    value={molecularData && molecularData.formula ?
                        molecularData.formula : undefined}>
                    <TextBoxButton name="chemicalFormulaSubscriptButton" location="after"
                        options={chemicalFormulaEditorButton as any} />
                    <Validator>
                        <StringLengthRule message="Molecular Formula must be less than 500 characters in length." max={500} />
                    </Validator>
                </TextBox>
            </div>

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">SMILES</div>
                <TextBox id="smiles" className="textbox"
                    showClearButton={true}
                    placeholder='NA' readOnly={!props.canEdit}
                    value={molecularData ?
                        molecularData.smiles : undefined}
                    onValueChanged={molecularDataFieldChanged} >
                    <Validator>
                        <StringLengthRule message="SMILES must be less than 500 characters in length." max={500} />
                    </Validator>
                </TextBox>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Molecular Weight</div>
                    <NumberBox id="weight" className="textbox"
                        showClearButton={true}
                        placeholder='NA' readOnly={!props.canEdit}
                        value={molecularData ? molecularData.weight : undefined}
                        onValueChanged={molecularDataFieldChanged}
                        format="#####.##" >
                        <Validator>
                            <RangeRule message="Molecular Weight must be between 0 and 99999.99" min={0} max={99999.99} />
                        </Validator>
                    </NumberBox>
                </div>
            </div>

            <div className="pw-comp-row">
                {molecularData && molecularData.svgImage && molecularData.svgImage.base64Blob &&
                    <div>
                        <div className="pw-form-unit-label pw-margin-bottom-05">SVG Image</div>
                        <div className="pw-form-unit molecular-image-medium centered-image-container">
                            {renderMolecularSvgImage()}
                        </div>

                        <div className="image-buttons-container pw-form-unit">
                            {props.canEdit &&
                                <>
                                    <div>
                                        <label htmlFor="molecularSvgImageFilePicker" className="file-picker-label">
                                            <i className="dx-icon-edit"></i> Update Image</label><br />
                                        <input id="molecularSvgImageFilePicker" className="file-picker-input"
                                            onChange={onMolecularSvgImageFileSelect} type="file" accept="image/svg+xml,.svg" />
                                    </div>
                                    <Button id="deleteMolecularImageButton" disabled={!props.canEdit}
                                        icon="trash" type="normal" text="Delete Image" onClick={onDeleteSvgImageButtonClick} />
                                </>
                            }
                        </div>
                    </div>
                }

                {props.canEdit && !molecularData || !molecularData.svgImage &&
                    <div className="pw-form-unit">
                        <div className="pw-margin-bottom-1">
                            <label htmlFor="molecularSvgImageFilePicker" className="file-picker-label">
                                <i className="dx-icon-plus"></i> Add SVG Image</label><br />
                            <input id="molecularSvgImageFilePicker" className="file-picker-input"
                                onChange={onMolecularSvgImageFileSelect} type="file" accept="image/svg+xml,.svg" />
                        </div>
                    </div>
                }

                {molecularData && molecularData.image && molecularData.image.base64Blob &&
                    <div>
                        <div className="pw-form-unit-label pw-margin-bottom-05">PNG Image</div>
                        <div className="pw-form-unit molecular-image-medium centered-image-container">
                            {renderMolecularPngImage()}
                        </div>

                        <div className="image-buttons-container pw-form-unit">
                            {props.canEdit &&
                                <>
                                    <div>
                                        <label htmlFor="molecularPngImageFilePicker" className="file-picker-label">
                                            <i className="dx-icon-edit"></i> Update Image</label><br />
                                        <input id="molecularPngImageFilePicker" className="file-picker-input"
                                            onChange={onMolecularPngImageFileSelect} type="file" accept="image/png" />
                                    </div>
                                    <Button id="deleteMolecularImageButton" disabled={!props.canEdit}
                                        icon="trash" type="normal" text="Delete Image" onClick={onDeletePngImageButtonClick} />
                                </>
                            }
                        </div>
                    </div>
                }

                {props.canEdit && !molecularData || !molecularData.image &&
                    <div className="pw-form-unit">
                        <div className="pw-margin-bottom-1">
                            <label htmlFor="molecularPngImageFilePicker" className="file-picker-label">
                                <i className="dx-icon-plus"></i> Add PNG Image</label><br />
                            <input id="molecularPngImageFilePicker" className="file-picker-input"
                                onChange={onMolecularPngImageFileSelect} type="file" accept="image/png" />
                        </div>
                    </div>
                }


                {/*
                {molecularData && molecularData.image && molecularData.image.base64Blob ?
                    <div className="image-buttons-container pw-form-unit">

                        {props.canEdit &&
                            <div>
                                <label htmlFor="molecularImageFilePicker" className="file-picker-label">
                                    <i className="dx-icon-edit"></i> Edit Image</label><br />
                                <input id="molecularImageFilePicker" className="file-picker-input"
                                    onChange={molecularImageFileSelect} type="file" />
                            </div>
                        }
                        {props.canEdit &&
                            <Button id="deleteMolecularImageButton" disabled={!props.canEdit}
                                icon="trash" type="normal" text="Delete Image" onClick={deleteImageButtonClick} />
                        }
                    </div> :
                    <div className="pw-form-unit">
                        {props.canEdit &&
                            <div className="pw-margin-bottom-1">
                                <label htmlFor="molecularImageFilePicker" className="file-picker-label">
                                    <i className="dx-icon-plus"></i> Add Image</label><br />
                                <input id="molecularImageFilePicker" className="file-picker-input"
                                    onChange={molecularImageFileSelect} type="file" />
                            </div>
                        }
                    </div>}
                    */}

            </div>

            <div>
                <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Open Babel Data</div>
                <div className="aurum-note">Experimental - add a SMILES and click SVG output</div>

                <div className="pw-comp-row">

                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Input Type</div>
                        <SelectBox id="scope" className="selectbox"
                            placeholder='NA'
                            displayExpr='description'
                            dataSource={openBabelTypes ? openBabelTypes : []}
                            onValueChanged={openBabelInputChanged}
                            value={openBabelInputType ? openBabelInputType : null} />
                    </div>
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Output Type</div>
                        <SelectBox id="scope" className="selectbox"
                            placeholder='NA'
                            displayExpr='description'
                            dataSource={openBabelTypes ? openBabelTypes : []}
                            onValueChanged={openBabelOutputChanged}
                            value={openBabelOutputType ? openBabelOutputType : null} />
                    </div>

                    <div className="pw-form-unit pw-comp-30">
                        <div className="pw-form-unit-label">Input</div>
                        <TextBox id="openBabelInputString" className="textbox"
                            onValueChanged={openBabelInputStringChanged}
                            placeholder='NA'
                            value={openBabelInputString ? openBabelInputString : undefined} />
                    </div>
                </div>

                <div className="pw-comp-row">
                    <Button className="pw-form-unit"
                        icon="sortup" text="OpenBabel" type="success"
                        hint="get OpenBabel data"
                        width='10rem' height='2rem'
                        onClick={onGetOpenBabelDataClick} />
                </div>

                {(molecularSvgImageXml && openBabelOutputType && openBabelOutputType.id === OpenBabelConvertFormats.Svg) &&
                    <div className="pw-comp-row">
                        <div className="pw-form-unit-label pw-margin-bottom-05">OpenBabel SVG Image(XML)</div>
                        <div className="pw-form-unit molecular-image-medium centered-image-container">
                            {renderOpenBabelMolecularSvgImage()}
                        </div>
                    </div>
                }


                {/* could be better/easier:
                {molecularSvgImageBase64 &&
                    <div className="pw-comp-row">
                        <div className="pw-form-unit-label pw-margin-bottom-05">OpenBabel SVG Image (Base64)</div>
                        <div className="pw-form-unit molecular-image-medium centered-image-container">
                            {renderOpenBabelMolecularSvgImageBase64()}
                        </div>
                    </div>
                }
                */}

                {openBabelMolecularData &&
                    <div className="pw-margin-top-1">
                        <TextArea height="16rem"
                            value={openBabelMolecularData ?
                                openBabelMolecularData : ''} />
                    </div>
                }

                {/*
                {molecularSvgImageXml &&
                    <div className="pw-comp-row">
                        <div className="pw-form-unit-label pw-margin-bottom-05">Response Data</div>
                        <div className="pw-form-unit">
                            {molecularSvgImageXml}
                        </div>
                    </div>
                }
                */}
            </div>

            <DmaFooter />

            {/*
            <label className="pw-form-unit-label">MOL File</label>
            <TextArea height={400}
                value={product && product.molecularData &&
                    product.molecularData.molFile ?
                    product.molecularData.molFile.text : ''}
                onValueChanged={molFileChanged}
            />
            */}

            {/*
            {
                casDataLoaderVisible &&
                <DialogPopup
                    visible={casDataLoaderVisible}
                    onHiding={hideCasDataloader}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="CAS Data Loader"
                    width="90vw"
                    height="90vh">

                    <CasDataLoader casNumber={molecularData && molecularData.casNumber ?
                        molecularData.casNumber : undefined}
                        key={`CAS${key}`}
                        onCasSave={saveCasData} />

                </DialogPopup>
            }
            */}
            {
                symbolEditorProps && symbolEditorProps.editorVisible &&
                <DialogPopup
                    visible={symbolEditorProps.editorVisible}
                    onHiding={hideSymbolEditor}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="Symbol Editor"
                    width={700}
                    height={450}>

                    <TextSymbolEditor key={`se${key}`}
                        textInput={symbolEditorProps.inputText}
                        propName={symbolEditorProps.propName}
                        onApply={textSymbolApply}
                        onCancel={textSymbolCancel} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div >
    );

    function openBabelInputChanged(e: any) {

        console.log("openBabelInputChanged()");
        console.log(e);


        setOpenBabelInputType(e.value);
    }

    function openBabelOutputChanged(e: any) {
        setOpenBabelOutputType(e.value);
    }

    function openBabelInputStringChanged(e: any) {
        setOpenBabelInputString(e.value);
    }

    async function onGetOpenBabelDataClick() {
        if (openBabelInputType && openBabelOutputType && openBabelInputString) {

            console.log("openBabelInputType:");
            console.log(openBabelInputType);
            console.log("openBabelOutputType:");
            console.log(openBabelOutputType);

            let request: OpenBabelRequest = {
                inputFormat: openBabelInputType.id,
                inputString: openBabelInputString, // "CCCCOc1ccccc1",
                outputFormat: openBabelOutputType.id, //OpenBabelConvertFormats.Svg
            };


            console.log(request);


            await getOpenBabelData(request)
                .then((data) => {
                    console.log("OPEN DATA..");
                    console.log(data);

                    if (data && data.outputStringBase64)
                        setMolecularSvgImageBase64(data.outputStringBase64);

                    if (data && data.outputString)
                        setMolecularSvgImageXml(data.outputString);

                    if (data && data.outputString)
                        setOpenBabelMolecularData(data.outputString);

                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving OpenBabel Data");
                });
        }
    }

    function textSymbolApply(data: TextSymbolEditorData) {
        if (symbolEditorProps && data) {
            let propName = data.propName;

            if (propName === "chemicalFormula") {
                let fieldUpdate: MolecularDataFieldUpdate = {
                    field: "formula",
                    value: data.text,
                    fieldUpdateType: FieldUpdateType.Data,
                    rowId: props.productId
                };

                if (molecularData) {
                    let editedMolecularData = { ...molecularData };
                    editedMolecularData.formula = data.text;

                    setMolecularData(editedMolecularData);
                }

                updateMolecularDataField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Applying Text Symbol");
                    });
            }

            hideSymbolEditor();
        }
    }

    function textSymbolCancel() {
        hideSymbolEditor();
    }

    function chemicalFormulaTextSymbolButton(e: any) {
        if (molecularData && molecularData.formula)
            showSymbolEditor("chemicalFormula", molecularData.formula);
    }

    function molecularDataFieldChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id as string;

        if (caller && molecularData) {
            const md = molecularData as any;
            const currentValue = md[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            if (!e.value && !e.previousValue)
                return;
            else {
                const prevMolecularData = { ...molecularData };

                setMolecularData({ ...molecularData, [caller]: e.value });

                let fieldUpdate: MolecularDataFieldUpdate = {
                    field: caller,
                    value: e.value,
                    fieldUpdateType: FieldUpdateType.Data,
                    rowId: molecularData.molecularDataId
                };

                updateMolecularDataField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Molecular Data Field");
                        setMolecularData(prevMolecularData);
                    });
            }
        }
    }

    //file functions

    function onMolecularSvgImageFileSelect(e: any) {
        let files = e.target.files;
        let file = files[0];

        if (files && file) {
            var reader = new FileReader();

            reader.onload = (readerEvt: any) => {
                let binaryString = readerEvt.target.result;
                let molecularImageData = btoa(binaryString);
                let imageFilename = "svgfile";
                let svgFilePicker = document.getElementById("molecularSvgImageFilePicker") as HTMLInputElement;

                if (svgFilePicker && svgFilePicker.files && svgFilePicker.files.length > 0) {
                    imageFilename = svgFilePicker.files[0].name;
                }

                if (molecularData) {
                    let image: AttachedImage = {
                        parentId: molecularData.molecularDataId,
                        name: imageFilename,
                        imageFormatType: ImageFormatType.Svg,
                        imageType: ImageType.SvgMolecularImage,
                        base64Blob: molecularImageData,
                        base64Bytes: molecularImageData
                    };

                    if (molecularData.svgImage && molecularData.svgImage.imageId) {
                        image.imageId = molecularData.svgImage.imageId;
                        updateImage(image)
                            .then(() => {
                                setMolecularData({ ...molecularData, svgImage: image });
                            })
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Updating SVG Image");
                            });
                    }
                    else {
                        insertImage(image)
                            .then((resp: any) => {
                                const savedSvg = {
                                    ...image,
                                    imageId: resp.imageId || resp
                                };
                                setMolecularData({ ...molecularData, svgImage: savedSvg });
                            })
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Adding New SVG Image");
                            });
                    }
                }
            };

            reader.readAsBinaryString(file);
        }
    }

    function onMolecularPngImageFileSelect(e: any) {
        let files = e.target.files;
        let file = files[0];

        if (files && file) {
            var reader = new FileReader();

            reader.onload = (readerEvt: any) => {
                let binaryString = readerEvt.target.result;
                let molecularImageData = btoa(binaryString);
                let imageFilename = "imagefile";
                let molecularImageFilePicker = document.getElementById("molecularPngImageFilePicker") as HTMLInputElement;

                if (molecularImageFilePicker && molecularImageFilePicker.files && molecularImageFilePicker.files.length > 0) {
                    imageFilename = molecularImageFilePicker.files[0].name;
                }

                if (molecularData) {
                    //let molecularData = product.molecularData;

                    let image: AttachedImage = {
                        parentId: molecularData.molecularDataId,
                        name: imageFilename,
                        imageFormatType: ImageFormatType.Png,
                        imageType: ImageType.MolecularImage,
                        base64Blob: molecularImageData
                    };

                    if (molecularData.image && molecularData.image.imageId) {
                        image.imageId = molecularData.image.imageId;
                        updateImage(image)
                            .then(() => {
                                setMolecularData({ ...molecularData, image: image });
                            })
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Updating PNG Image");
                            });
                    }
                    else {
                        insertImage(image)
                            .then((resp: any) => {
                                const savedImg = {
                                    ...image,
                                    imageId: resp.imageId || resp
                                };
                                setMolecularData({ ...molecularData, image: savedImg });
                            })
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Adding New PNG Image");
                            });
                    }                    

                    /*
                    setProduct({
                        ...product, molecularData: {
                            ...product.molecularData,
                            image: image
                        }
                    });
                    */
                }
            };

            reader.readAsBinaryString(file);
        }
    }

    function onDeleteSvgImageButtonClick(e: any) {
        if (!molecularData || !molecularData.svgImage || !molecularData.svgImage.imageId)
            return;

        deleteImage(molecularData.svgImage.imageId)
            .then(() => {
                setMolecularData({ ...molecularData, svgImage: null });
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Deleting SVG Image");
            });;
    }

    function onDeletePngImageButtonClick(e: any) {
        if (!molecularData || !molecularData.image || !molecularData.image.imageId)
            return;

        deleteImage(molecularData.image.imageId)
            .then(() => {
                setMolecularData({ ...molecularData, image: null });
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Deleting PNG Image");
            });;
    }

    /*
    async function saveCasData(casChemical: CasChemical) {
        console.log("casChemical:");
        console.log(casChemical);

        if (!casChemical || !molecularData || !molecularData.molecularDataId)
            return;

        //why is this needed??
        //casChemical.productId = productId;

        casChemical.molecularDataId = molecularData.molecularDataId;

        if (molecularData.image && molecularData.image.imageId)
            casChemical.imageId = molecularData.image.imageId;

        await updateCasData(casChemical)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating CAS Data");
            });


        getMolecularData();
        hideCasDataloader();
    }
    */

    async function getMolecularData() {
        if (props.productId) {
            await CrudFunctions.getData(`products/moleculardatabyproductid/${props.productId}`)
                .then((data) => {
                    setMolecularData(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Molecular Data");
                });
        }
    }

    async function getOpenBabelFormats() {
        if (props.productId) {
            await CrudFunctions.getData("common/openbabelformats")
                .then((data) => {
                    setOpenBabelTypes(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Open Babel Formats");
                });
        }
    }

    async function getOpenBabelData(request: OpenBabelRequest) {
        return await CrudFunctions.postData("chemistry/openbabelconvert", request);
    }

    async function insertImage(image: AttachedImage) {
        return CrudFunctions.postData("media/image", image);
    }

    /*
    async function updateCasData(casData: CasChemical) {
        return CrudFunctions.putData("products/casdata", casData);
    }
    */

    async function updateMolecularDataField(fieldUpdate: MolecularDataFieldUpdate) {
        return CrudFunctions.putData("products/moleculardatafield", fieldUpdate);
    }

    async function updateImage(image: AttachedImage) {
        return CrudFunctions.putData("media/image", image);
    }

    async function deleteImage(imageId: string) {
        return CrudFunctions.deleteData(`media/image/${imageId}`);
    }

    async function getAllData() {
        await Promise.all([
            getOpenBabelFormats(),
            getMolecularData()
        ])
            .then(() => setDataLoaded(true));
    }

    //show/hide

    /*
    function showCasDataloader() {
        setCasDataLoaderVisible(true);
    }
    
    function hideCasDataloader() {
        setCasDataLoaderVisible(false);
    }
    */

    function showSymbolEditor(propName: string, text?: string) {
        let symbolEditorParams: SymbolEditorProps = {
            inputText: text,
            propName: propName,
            editorVisible: true
        };

        setSymbolEditorProps(symbolEditorParams);
    }

    function hideSymbolEditor() {
        let symbolEditorParams = { ...symbolEditorProps };
        symbolEditorParams.editorVisible = false;

        setSymbolEditorProps(symbolEditorParams);
    }

    //other
    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
